import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Button,
    Box,
    Typography,
    CircularProgress
} from '@mui/material';
import { BookOpen, Lock } from 'lucide-react';
import LoginForm from '../components/LoginForm';
import RegisterForm from '../components/RegisterForm';
import { useAuthStore } from '../stores';

const AssignmentLinkAccess = () => {
    const { t } = useTranslation();
    const { token } = useParams();
    const { isAuthenticated } = useAuthStore();
    const [showLogin, setShowLogin] = useState(false);
    const [showRegister, setShowRegister] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);

    // Note: We don't auto-redirect authenticated users here because:
    // 1. This page should only be reached for unauthenticated users
    // 2. If an authenticated user reaches here, they should manually retry
    // 3. This prevents infinite redirect loops

    const handleLoginSuccess = async (userToken) => {
        setShowLogin(false);
        setIsRedirecting(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        window.location.href = `${backendUrl}/client-api/v1/lti/links/${token}/launch/`;
    };

    const handleRegisterSuccess = async () => {
        setShowRegister(false);
        setIsRedirecting(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        window.location.href = `${backendUrl}/client-api/v1/lti/links/${token}/launch/`;
    };

    if (isRedirecting) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
                <Box className="text-center">
                    <CircularProgress size={60} className="mb-4" />
                    <Typography variant="h6" className="text-gray-700">
                        {t('assignment_link.redirecting')}
                    </Typography>
                </Box>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
                <div className="text-center mb-8">
                    <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                        <BookOpen className="w-8 h-8 text-blue-600" />
                    </div>
                    <Typography variant="h4" className="font-bold text-gray-900 mb-2">
                        {t('assignment_link.access_required')}
                    </Typography>
                    <Typography variant="body1" className="text-gray-600">
                        {isAuthenticated
                            ? t('assignment_link.already_authenticated')
                            : t('assignment_link.access_description')
                        }
                    </Typography>
                </div>

                <div className="space-y-3">
                    {isAuthenticated ? (
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={() => {
                                setIsRedirecting(true);
                                const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
                                window.location.href = `${backendUrl}/client-api/v1/lti/links/${token}/launch/`;
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg"
                        >
                            {t('assignment_link.continue')}
                        </Button>
                    ) : (
                        <>
                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                onClick={() => setShowLogin(true)}
                                className="bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg"
                            >
                                {t('assignment_link.sign_in')}
                            </Button>

                            <Button
                                fullWidth
                                variant="outlined"
                                size="large"
                                onClick={() => setShowRegister(true)}
                                className="border-blue-600 text-blue-600 hover:bg-blue-50 py-3 rounded-lg"
                            >
                                {t('assignment_link.create_account')}
                            </Button>
                        </>
                    )}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex items-start gap-2 text-sm text-gray-500">
                        <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <Typography variant="caption" className="text-gray-500">
                            {t('assignment_link.security_note')}
                        </Typography>
                    </div>
                </div>
            </div>

            <Dialog open={showLogin} onClose={() => setShowLogin(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('assignment_link.sign_in')}</DialogTitle>
                <DialogContent>
                    <LoginForm
                        onLoginSuccess={handleLoginSuccess}
                        onClose={() => setShowLogin(false)}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={showRegister} onClose={() => setShowRegister(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('assignment_link.create_account')}</DialogTitle>
                <DialogContent>
                    <RegisterForm
                        onRegisterSuccess={handleRegisterSuccess}
                        onClose={() => setShowRegister(false)}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AssignmentLinkAccess;
